import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import routes from './routes';
import ProtectedRoute from './ProtectedRoute';

import AuthLayout from 'layouts/AuthLayout';
import AdminLayout from 'layouts/AdminLayout';
import FormWithFooterLayout from 'layouts/FormWithFooterLayout';
import PersonalLayout from 'layouts/PersonalLayout';
import MyMeetSignup from 'pages/personal/MyMeetSignup';
import MeetOnboardingWizard from 'pages/personal/MeetOnboardingWizard';

/* Auth Pages */
import Login from 'pages/auth/Login';
import VerifyEmail from 'pages/auth/VerifyEmail';
import SetPassword from 'pages/auth/SetPassword';
import ForgotPassword from 'pages/auth/ForgotPassword';
import ResetPassword from 'pages/auth/ResetPassword';

/* Admin Page*/
import ClubDashboard from 'pages/admin/ClubDashboard';
import AthleteDashboard from 'pages/member/AthleteDashboard';

import ClubRecords from 'pages/admin/ClubRecords';

import Competitions from 'pages/admin/Meets';
import AddMeet from 'pages/admin/AddMeet';
import ShowMeet from 'pages/admin/ShowMeet';
import EditMeet from 'pages/admin/EditMeet';
import Events from 'pages/admin/Events';
import Results from 'pages/admin/Results';
import TrainingResults from 'pages/admin/TrainingResults';

import MyProfile from 'pages/admin/MyProfile';

import PersonalDashboard from 'pages/personal/PersonalDashboard';

import ClubMembers from 'pages/admin/ClubMembers';
//import MemberImport from 'pages/admin/MemberImport';  // Moved this to new ImportPage
import ImportPage from 'pages/admin/ImportPage';
import AddMember from 'pages/admin/AddMember';
import ShowMember from 'pages/admin/ShowMember';
import EditMember from 'pages/admin/EditMember';
import AdminAthleteResultsPage from 'pages/admin/Athletes/AdminAthleteResultsPage';

import Athletes from 'pages/admin/Athletes';
import Teams from 'pages/admin/Teams';

import AddUser from 'pages/admin/AddUser';
import AddAthlete from 'pages/admin/AddAthlete';
import EditAthlete from 'pages/admin/EditAthlete';

import Homepage from 'pages/client/Homepage';
import NotFound from 'pages/NotFound';
import Season from 'pages/admin/ClubSettings/Season';
import ClubSettings from 'pages/admin/ClubSettings';

import Volunteer from 'pages/admin/Volunteer';
import VolunteerOpportunities from 'components/dashboard/VolunteerOpportunities';
import { useLocation } from 'react-router-dom';
import VolunteerStatus from 'pages/member/VolunteerStatus';
import VolunteerRoles from 'pages/admin/ClubSettings/VolunteerRoles';
import VolunteerHoursTargets from 'pages/admin/ClubSettings/VolunteerHoursTargets';
import { UserPermissionLevel } from 'constants/auth';
import AgeGroups from 'pages/admin/ClubSettings/AgeGroups';
import AthleteGroups from 'pages/admin/ClubSettings/AthleteGroups';
import EventTypes from 'pages/admin/ClubSettings/EventTypes';
import MeetRegistration from 'pages/admin/MeetRegistration';
import MyMeetRegistrations from 'pages/admin/MyMeetRegistrations';
import PublicMeetSignup from 'pages/public/PublicMeetSignup';
import VisitorMembers from 'pages/admin/VisitorMembers';
import VisitorAthletes from 'pages/admin/VisitorAthletes';

import AthleteSeasonPointsPage from 'pages/admin/Reports/AthletePoints/AthleteSeasonPointsPage';
import HousePointsPage from 'pages/admin/Reports/HousePoints';
import SeasonChampionsPage from 'pages/admin/Reports/SeasonChampions';
import MemberVolunteerPointsPage from 'pages/admin/Reports/MemberVolunteerPoints/MemberVolunteerPointsPage';
import MeetPointsDashboardPage from 'pages/admin/Reports/MeetPointsDashboard/MeetPointsDashboardPage';

import AthletePRs from 'pages/admin/Reports/AthletePRs';
import EventParticipationPage from 'pages/admin/Reports/EventParticipation';
import AthleteAttendancePage from 'pages/admin/Reports/AthleteAttendance';
import MostMeetPRsPage from 'pages/admin/Reports/MostMeetPRs';
import SeasonEventPerformancePage from 'pages/admin/Reports/SeasonEventPerformance';
import ResultsSyncPage from 'pages/admin/Reports/ResultsSync';

import StartListsDemo from 'components/admin/StartLists/Demo_StartListsDemo';
import StartListsView from 'components/admin/StartLists/StartListsView/StartListsView';
import StartListDetails from 'components/admin/StartLists/StartListDetails/StartListDetails';
import StartListEditor from 'components/admin/StartLists/StartListsEditor/StartListsEditor';

const RouteConfig = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0); // Reset scroll to top on route change
  }, [pathname]);

  return (
    <Routes>
      <Route path={routes.client.home} element={<Homepage />} />
      {/* Public, unauthenticated meet landing page (§6.1) — opaque short code, minimal public shell. */}
      <Route path={routes.public.meetSignup.path} element={<PublicMeetSignup />} />

      <Route element={<AuthLayout />}>
        <Route path={routes.auth.login} element={<Login />} />
        <Route path={routes.auth.verifyEmail} element={<VerifyEmail />} />
        <Route path={routes.auth.setPassword} element={<SetPassword />} />
        <Route path={routes.auth.forgotPassword} element={<ForgotPassword />} />
        <Route path={routes.auth.resetPassword} element={<ResetPassword />} />
      </Route>

      <Route element={<FormWithFooterLayout />}>
        <Route
          path={routes.admin.addUser.path}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Admin}>
              <AddUser />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.members.addAthlete.path}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Admin}>
              <AddAthlete />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.members.editAthlete.path}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Admin}>
              <EditAthlete />
            </ProtectedRoute>
          }
        />
        {/* REMOVED: AddMeet and EditMeet routes moved to AdminLayout below */}
        <Route
          path={routes.admin.members.addMember.path}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Admin}>
              <AddMember />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.members.editMember.path}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Admin}>
              <EditMember />
            </ProtectedRoute>
          }
        />
      </Route>

      <Route element={<AdminLayout />}>
        <Route
          path={routes.admin.dashboard}
          element={
            <ProtectedRoute>
              <ClubDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.athleteDashboard}
          element={
            <ProtectedRoute>
              <AthleteDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.volunteerStatus}
          element={
            <ProtectedRoute>
              <VolunteerStatus />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.members.athletes}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Official}>
              <Athletes />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.members.teams}
          element={
            <ProtectedRoute
              permissionLevel={UserPermissionLevel.Official}
              feature="teams"
            >
              <Teams />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.members.athleteResults.path}
          element={
            <ProtectedRoute>
              <AdminAthleteResultsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.clubRecord}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Guest}>
              <ClubRecords />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.meets}
          element={
            <ProtectedRoute>
              <Competitions />
            </ProtectedRoute>
          }
        />
        {/* MOVED: Meet routes from FormWithFooterLayout to AdminLayout */}
        <Route
          path={routes.admin.addMeet.path}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Admin}>
              <AddMeet />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.editMeet.path}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Official}>
              <EditMeet />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.meetGeneration.path}
          element={
            <ProtectedRoute>
              <MeetRegistration />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.myMeetRegistrations.path}
          element={
            <ProtectedRoute>
              <MyMeetRegistrations />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.meetRegistrationImport.path}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Admin}>
              <ImportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.meetStartLists.path}
          element={
            <ProtectedRoute>
              <StartListsView />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.meetStartListDetails.path}
          element={
            <ProtectedRoute>
              <StartListDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.meetStartListEditor.path}
          element={
            <ProtectedRoute>
              <StartListEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.meetPointsDashboard.path}
          element={
            <ProtectedRoute>
              <MeetPointsDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.showMeet.path}
          element={
            <ProtectedRoute>
              <ShowMeet />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.events.path}
          element={
            <ProtectedRoute>
              <Events />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.addEvent.path}
          element={
            <ProtectedRoute>
              <Events isOpenEditPopup={true} />
            </ProtectedRoute>
          }
        />

        <Route
          path={routes.admin.editEvent.path}
          element={
            <ProtectedRoute>
              <Events isOpenEditPopup={true} />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.results.path}
          element={
            <ProtectedRoute>
              <Results />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.ageView.path}
          element={
            <ProtectedRoute>
              <Results />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.eventRound.path}
          element={
            <ProtectedRoute>
              <Results />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.trainingResults.path}
          element={
            <ProtectedRoute>
              <TrainingResults />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.editResults.path}
          element={
            <ProtectedRoute>
              <Results isOpenEditPopup={true} isOpenEditEventPopup={true} />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.members.members}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Official}>
              <ClubMembers />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.members.showMember.path}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Guest}>
              <ShowMember />
            </ProtectedRoute>
          }
        />
        {/* The Guest pages are hidden for every club (Doug, 2026-10-11): an old
            link or bookmark goes to the dashboard. The pages are still in
            pages/admin/GuestMembers and pages/admin/GuestAthletes; see the note
            in config/admin/menu.js. */}
        <Route
          path={routes.admin.guests.members}
          element={<Navigate to={routes.admin.dashboard} replace />}
        />
        <Route
          path={routes.admin.guests.athletes}
          element={<Navigate to={routes.admin.dashboard} replace />}
        />
        {/* The Visitor pages are for a club that has visitors; a school has none. */}
        <Route
          path={routes.admin.visitors.members}
          element={
            <ProtectedRoute
              permissionLevel={UserPermissionLevel.Official}
              feature="visitors"
            >
              <VisitorMembers />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.visitors.athletes}
          element={
            <ProtectedRoute
              permissionLevel={UserPermissionLevel.Official}
              feature="visitors"
            >
              <VisitorAthletes />
            </ProtectedRoute>
          }
        />
        <Route
          path="/members/import"
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Admin}>
              <ImportPage />
            </ProtectedRoute>
          }
        />
        <Route path={routes.admin.profile} element={<MyProfile />} />
       <Route
          path={routes.admin.reports.athleteSeasonPoints}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Official}>
              <AthleteSeasonPointsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.reports.seasonChampions}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Official}>
              <SeasonChampionsPage />
            </ProtectedRoute>
          }
        />
        {/* House Points is a school's report (points slice 5): a club has no houses. */}
        <Route
          path={routes.admin.reports.housePoints}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Official} feature="teams">
              <HousePointsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.reports.memberVolunteerPoints}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Official}>
              <MemberVolunteerPointsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.reports.athletePRs}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Official}>
              <AthletePRs />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.reports.eventParticipation}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Official}>
              <EventParticipationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.reports.athleteAttendance}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Official}>
              <AthleteAttendancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.reports.mostMeetPRs}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Official}>
              <MostMeetPRsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.reports.seasonEventPerformance}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Official}>
              <SeasonEventPerformancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.reports.resultsSync}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Admin}>
              <ResultsSyncPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.volunteerManagement.path}
          element={
            <ProtectedRoute>
              <Volunteer />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.volunteerOpportunities.path}
          element={
            <ProtectedRoute>
              <VolunteerOpportunities />
            </ProtectedRoute>
          }
        />
        {/* Club Settings is for Admins and Owners only (Doug, 2026-10-11): every
            page under it asks for Admin, matching the menu. */}
        <Route
          path={routes.admin.clubSettings.general}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Admin}>
              <ClubSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.clubSettings.ageGroups}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Admin}>
              <AgeGroups />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.clubSettings.athleteGroups}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Admin}>
              <AthleteGroups />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.clubSettings.eventTypes}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Admin}>
              <EventTypes />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.clubSettings.volunteerRoles}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Admin}>
              <VolunteerRoles />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.clubSettings.volunteerHoursTargets}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Admin}>
              <VolunteerHoursTargets />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.admin.clubSettings.manageSeasons.path}
          element={
            <ProtectedRoute permissionLevel={UserPermissionLevel.Admin}>
              <Season />
            </ProtectedRoute>
          }
        />
        <Route
          path="/demo/start-lists"
          element={
            <ProtectedRoute>
              <StartListsDemo />
            </ProtectedRoute>
          }
        />
      </Route>

      <Route element={<PersonalLayout />}>
        <Route
          path={routes.personal.home}
          element={
            <ProtectedRoute>
              <Navigate to={routes.personal.profile} replace />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.personal.profile}
          element={
            <ProtectedRoute>
              <PersonalDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.personal.athletes}
          element={
            <ProtectedRoute>
              <Navigate to={routes.personal.profile} replace />
            </ProtectedRoute>
          }
        />
        {/* Case 2 (§6.6) — non-host-club user's meet registrations, inside the personal portal shell. */}
        <Route
          path={routes.personal.meetRegistrations.path}
          element={
            <ProtectedRoute>
              <MyMeetSignup />
            </ProtectedRoute>
          }
        />
        {/* Case 3 (§6.6) — the guided onboarding wizard for a brand-new registrant. */}
        <Route
          path={routes.personal.meetOnboard.path}
          element={
            <ProtectedRoute>
              <MeetOnboardingWizard />
            </ProtectedRoute>
          }
        />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default RouteConfig;
